import {FC, useCallback, useEffect, useRef, useState} from 'react'
import {api, ServiceCategory, ServiceGroup, fileUrl} from '../../services/api'
import {toast} from '../../utils/toast'

// ── Icon Picker ───────────────────────────────────────────────────────────────
// A sub-category's icon is what the mobile app draws in the round chip above a
// category's service list, and on the cards of the items filed under it.
const IconPicker: FC<{
  preview: string | null
  onSelect: (file: File) => void
  onRemove: () => void
  // The card photo is cropped to fill; an icon is shown whole.
  fit?: 'contain' | 'cover'
  width?: number
  // Names the empty box, so two pickers side by side are told apart without
  // reading the note underneath them.
  label?: string
  glyph?: 'icon' | 'photo'
}> = ({
  preview,
  onSelect,
  onRemove,
  fit = 'contain',
  width = 140,
  label = 'Upload',
  glyph = 'photo',
}) => {
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <div>
      <input ref={inputRef} type='file' accept='image/*' style={{display: 'none'}}
        onChange={e => { const f = e.target.files?.[0]; if (f) onSelect(f); e.target.value = '' }} />

      {preview ? (
        <div style={{position: 'relative', width, height: 140, borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0', background: '#fff'}}>
          <img src={preview} alt='icon' style={{width: '100%', height: '100%', objectFit: fit}} />
          <div style={{position: 'absolute', bottom: 0, left: 0, right: 0, display: 'flex'}}>
            <button type='button' onClick={() => inputRef.current?.click()}
              style={{flex: 1, padding: '7px 0', border: 'none', background: 'rgba(15,23,42,0.78)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer'}}>
              Change
            </button>
            <button type='button' onClick={onRemove}
              style={{flex: 1, padding: '7px 0', border: 'none', background: 'rgba(220,38,38,0.85)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer'}}>
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div onClick={() => inputRef.current?.click()}
          style={{
            width, height: 140, borderRadius: 12, cursor: 'pointer',
            border: '2px dashed #cbd5e1', background: '#f8fafc',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
            textAlign: 'center', padding: 8, boxSizing: 'border-box',
          }}>
          {glyph === 'icon' ? (
            // A badge in its rounded square — the shape the app draws it in.
            <svg width='28' height='28' viewBox='0 0 24 24' fill='none' stroke='#94a3b8' strokeWidth='2'>
              <rect x='3' y='3' width='18' height='18' rx='5' ry='5'/>
              <path d='M12 8.2l1.2 2.5 2.8.4-2 2 .5 2.7-2.5-1.3-2.5 1.3.5-2.7-2-2 2.8-.4z' strokeLinejoin='round'/>
            </svg>
          ) : (
            <svg width='28' height='28' viewBox='0 0 24 24' fill='none' stroke='#94a3b8' strokeWidth='2'>
              <rect x='3' y='3' width='18' height='18' rx='2' ry='2'/><circle cx='8.5' cy='8.5' r='1.5'/><polyline points='21 15 16 10 5 21'/>
            </svg>
          )}
          <span style={{fontSize: 12, color: '#94a3b8', fontWeight: 600}}>{label}</span>
          <span style={{fontSize: 11, color: '#cbd5e1'}}>Click to upload</span>
        </div>
      )}
    </div>
  )
}

// ── Checkbox ──────────────────────────────────────────────────────────────────
// Where a sub-category is shown. Deliberately a checkbox and not a toggle: the
// two are independent and often both on, which a pair of switches would read as
// a choice between them.
const ShowOn: FC<{
  checked: boolean
  onChange: () => void
  title: string
  hint: string
}> = ({checked, onChange, title, hint}) => (
  <label style={{
    display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1, cursor: 'pointer',
    padding: '12px 14px', borderRadius: 8,
    border: `1px solid ${checked ? '#bfdbfe' : '#e2e8f0'}`,
    background: checked ? '#eff6ff' : '#f8fafc',
  }}>
    <input type='checkbox' checked={checked} onChange={onChange} style={{marginTop: 2, cursor: 'pointer'}} />
    <span>
      <span style={{display: 'block', fontSize: 13, fontWeight: 600, color: '#374151'}}>{title}</span>
      <span style={{display: 'block', fontSize: 11, color: '#94a3b8', marginTop: 2}}>{hint}</span>
    </span>
  </label>
)

// ── Toggle Switch ─────────────────────────────────────────────────────────────
const Toggle: FC<{checked: boolean; onChange: () => void}> = ({checked, onChange}) => (
  <div onClick={onChange} style={{
    width: 40, height: 22, borderRadius: 11, cursor: 'pointer', flexShrink: 0,
    background: checked ? '#22c55e' : '#e2e8f0',
    position: 'relative', transition: 'background 0.2s',
  }}>
    <div style={{
      position: 'absolute', top: 3, left: checked ? 21 : 3,
      width: 16, height: 16, borderRadius: '50%', background: '#fff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s',
    }} />
  </div>
)

// ── Main Page ─────────────────────────────────────────────────────────────────
const SubCategoriesPage: FC = () => {
  const [categories, setCategories] = useState<ServiceCategory[]>([])
  const [groups, setGroups] = useState<ServiceGroup[]>([])
  const [filterCategory, setFilterCategory] = useState('')   // '' = every category
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [showModal, setShowModal]       = useState(false)
  const [editTarget, setEditTarget]     = useState<ServiceGroup | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<ServiceGroup | null>(null)
  const [saving, setSaving]             = useState(false)

  const [formCategoryId, setFormCategoryId] = useState('')
  const [formLabel, setFormLabel]           = useState('')
  // The line under the title on the Home Service card.
  const [formDescription, setFormDescription] = useState('')
  // Which of the two customer screens this sub-category appears on.
  const [formShowHome, setFormShowHome] = useState(false)
  const [formShowPickup, setFormShowPickup] = useState(true)
  const [formOrder, setFormOrder]           = useState('')
  // The chip glyph and card badge.
  const [formIconFile, setFormIconFile]     = useState<File | null>(null)
  const [formIconPreview, setFormIconPreview] = useState<string | null>(null)
  const [formRemoveIcon, setFormRemoveIcon] = useState(false)
  // The photo across the top of the card.
  const [formImageFile, setFormImageFile]   = useState<File | null>(null)
  const [formImagePreview, setFormImagePreview] = useState<string | null>(null)
  const [formRemoveImage, setFormRemoveImage] = useState(false)
  const [formActive, setFormActive]         = useState(true)
  const [formError, setFormError]           = useState('')

  useEffect(() => {
    api.getServiceCategories().then(setCategories).catch(() => {})
  }, [])

  const reload = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setGroups(await api.getServiceGroups(filterCategory || undefined))
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load sub-categories')
    } finally {
      setLoading(false)
    }
  }, [filterCategory])
  useEffect(() => { reload() }, [reload])

  const categoryName = (id: string) => categories.find(c => c.id === id)?.label ?? '—'

  // Every category, including the 'packages' ones. A sub-category used to be
  // only the round filter row above a list, which a feature checklist has no
  // use for — but it is now also a Home Service card, and those a customer
  // browses under any category.
  const groupableCategories = categories

  const visible = groups.filter(g =>
    !search.trim() || g.label.toLowerCase().includes(search.trim().toLowerCase()),
  )

  const openAdd = () => {
    setEditTarget(null)
    setFormCategoryId(filterCategory || groupableCategories[0]?.id || '')
    setFormLabel(''); setFormDescription(''); setFormOrder('')
    setFormShowHome(false); setFormShowPickup(true)
    setFormPictures(null, null)
    setFormActive(true); setFormError('')
    setShowModal(true)
  }

  const openEdit = (g: ServiceGroup) => {
    setEditTarget(g)
    setFormCategoryId(g.categoryId)
    setFormLabel(g.label); setFormDescription(g.description ?? ''); setFormOrder(String(g.order))
    setFormShowHome(g.showInHomeService); setFormShowPickup(g.showInPickup)
    setFormPictures(g.icon ? fileUrl(g.icon) : null, g.image ? fileUrl(g.image) : null)
    setFormActive(g.isActive); setFormError('')
    setShowModal(true)
  }

  // Both pictures back to what is stored, with nothing picked or pending
  // removal — what opening the modal always starts from.
  const setFormPictures = (icon: string | null, image: string | null) => {
    setFormIconFile(null); setFormIconPreview(icon); setFormRemoveIcon(false)
    setFormImageFile(null); setFormImagePreview(image); setFormRemoveImage(false)
  }

  const handleSave = async () => {
    if (!formLabel.trim()) { setFormError('Name is required.'); return }
    if (!editTarget && !formCategoryId) { setFormError('Pick a parent category.'); return }
    setSaving(true)
    try {
      if (editTarget) {
        // The parent category is fixed after creation — moving a sub-category
        // would orphan the items filed under it.
        await api.updateServiceGroup(editTarget.id, {
          label: formLabel.trim(),
          description: formDescription.trim(),
          showInHomeService: formShowHome,
          showInPickup: formShowPickup,
          order: formOrder ? parseInt(formOrder) : editTarget.order,
          isActive: formActive,
          icon: formIconFile,
          removeIcon: formRemoveIcon,
          image: formImageFile,
          removeImage: formRemoveImage,
        })
      } else {
        await api.createServiceGroup({
          categoryId: formCategoryId,
          label: formLabel.trim(),
          description: formDescription.trim(),
          showInHomeService: formShowHome,
          showInPickup: formShowPickup,
          order: formOrder ? parseInt(formOrder) : groups.length,
          icon: formIconFile,
          image: formImageFile,
        })
      }
      setShowModal(false)
      reload()
      toast(editTarget ? 'Sub-category updated' : 'Sub-category created')
    } catch (e: any) {
      setFormError(e?.message ?? 'Failed to save.')
      toast(e?.message ?? 'Save failed', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await api.deleteServiceGroup(deleteTarget.id)
      setDeleteTarget(null)
      reload()
      toast('Sub-category deleted')
    } catch (e: any) {
      setError(e?.message ?? 'Delete failed')
      toast(e?.message ?? 'Delete failed', 'error')
    }
  }

  const toggleStatus = async (g: ServiceGroup) => {
    try {
      await api.updateServiceGroup(g.id, {isActive: !g.isActive})
      reload()
      toast(g.isActive ? 'Sub-category disabled' : 'Sub-category activated')
    } catch (e: any) {
      setError(e?.message ?? 'Status update failed')
      toast(e?.message ?? 'Status update failed', 'error')
    }
  }

  const inputStyle = {
    width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0',
    borderRadius: 8, fontSize: 14, color: '#0f172a', background: '#f8fafc',
    outline: 'none', boxSizing: 'border-box' as const,
  }

  const labelStyle = {
    fontSize: 13, fontWeight: 600 as const, color: '#374151',
    display: 'block' as const, marginBottom: 6,
  }

  return (
    <div>
      {/* Header */}
      <div style={{marginBottom: 24}}>
        <h1 style={{fontSize: 24, fontWeight: 700, color: '#0f172a', margin: '0 0 4px'}}>Sub Categories</h1>
        <p style={{fontSize: 14, color: '#64748b', margin: 0}}>
          Groups within a service category — the round filter row above that category's list, and the cards on the app's Home Service screen
        </p>
      </div>

      {/* Toolbar */}
      <div style={{
        background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
        padding: '16px 20px', marginBottom: 16,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
      }}>
        <div style={{position: 'relative', flex: 1, minWidth: 200}}>
          <svg style={{position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8'}} width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <circle cx='11' cy='11' r='8'/><line x1='21' y1='21' x2='16.65' y2='16.65'/>
          </svg>
          <input type='text' placeholder='Search sub-categories...' value={search}
            onChange={e => setSearch(e.target.value)}
            style={{...inputStyle, paddingLeft: 38, background: '#fff'}} />
        </div>
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
          style={{...inputStyle, width: 'auto', minWidth: 200, flex: '0 1 auto'}}>
          <option value=''>All categories</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <button onClick={reload} disabled={loading}
          style={{display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontWeight: 500, color: '#374151', background: '#f8fafc', cursor: 'pointer'}}>
          <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' style={{animation: loading ? 'spin 1s linear infinite' : 'none'}}>
            <polyline points='23 4 23 10 17 10'/><polyline points='1 20 1 14 7 14'/>
            <path d='M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15'/>
          </svg>
          Refresh
        </button>
        <button onClick={openAdd} disabled={!groupableCategories.length}
          style={{display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#fff', background: '#3b82f6', cursor: 'pointer'}}>
          <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'><line x1='12' y1='5' x2='12' y2='19'/><line x1='5' y1='12' x2='19' y2='12'/></svg>
          Add Sub Category
        </button>
      </div>

      {error && (
        <div style={{padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 14, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <span>{error}</span>
          <button onClick={() => { setError(''); reload() }} style={{color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: 13}}>Retry</button>
        </div>
      )}

      {/* Table */}
      <div style={{background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden'}}>
        {loading ? (
          <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 0'}}>
            <div style={{width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite'}} />
          </div>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
              <thead>
                <tr style={{background: '#f8fafc', borderBottom: '1px solid #e2e8f0'}}>
                  {['ORDER', 'ICON', 'CARD IMAGE', 'SUB CATEGORY', 'CATEGORY', 'STATUS', 'ACTIONS'].map(h => (
                    <th key={h} style={{padding: '11px 20px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((g, i) => (
                  <tr key={g.id}
                    style={{borderBottom: i < visible.length - 1 ? '1px solid #f1f5f9' : 'none'}}
                    onMouseEnter={ev => (ev.currentTarget.style.background = '#fafafa')}
                    onMouseLeave={ev => (ev.currentTarget.style.background = 'transparent')}
                  >
                    <td style={{padding: '14px 20px', width: 80}}>
                      <span style={{fontSize: 13, fontWeight: 600, color: '#94a3b8', background: '#f1f5f9', padding: '3px 10px', borderRadius: 20}}>
                        #{g.order}
                      </span>
                    </td>

                    <td style={{padding: '14px 20px', width: 90}}>
                      {g.icon ? (
                        <img src={fileUrl(g.icon)} alt={g.label}
                          style={{width: 56, height: 56, borderRadius: 10, objectFit: 'contain', border: '1px solid #e2e8f0', background: '#fff'}} />
                      ) : (
                        <div style={{width: 56, height: 56, borderRadius: 10, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1'}}>
                          <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                            <rect x='3' y='3' width='18' height='18' rx='2' ry='2'/><circle cx='8.5' cy='8.5' r='1.5'/><polyline points='21 15 16 10 5 21'/>
                          </svg>
                        </div>
                      )}
                    </td>

                    <td style={{padding: '14px 20px', width: 120}}>
                      {g.image ? (
                        <img src={fileUrl(g.image)} alt={g.label}
                          style={{width: 92, height: 56, borderRadius: 10, objectFit: 'cover', border: '1px solid #e2e8f0', background: '#fff'}} />
                      ) : (
                        <div style={{width: 92, height: 56, borderRadius: 10, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1'}}>
                          <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                            <rect x='3' y='3' width='18' height='18' rx='2' ry='2'/><circle cx='8.5' cy='8.5' r='1.5'/><polyline points='21 15 16 10 5 21'/>
                          </svg>
                        </div>
                      )}
                    </td>

                    <td style={{padding: '14px 20px', minWidth: 180}}>
                      <span style={{fontWeight: 700, color: '#0f172a', fontSize: 14}}>{g.label}</span>
                      <span style={{display: 'flex', gap: 4, marginTop: 4}}>
                        {g.showInHomeService && <span style={{padding: '1px 7px', borderRadius: 999, fontSize: 10, fontWeight: 700, color: '#0369a1', background: '#e0f2fe'}}>HOME SERVICE</span>}
                        {g.showInPickup && <span style={{padding: '1px 7px', borderRadius: 999, fontSize: 10, fontWeight: 700, color: '#4338ca', background: '#e0e7ff'}}>PICKUP</span>}
                        {!g.showInHomeService && !g.showInPickup && <span style={{padding: '1px 7px', borderRadius: 999, fontSize: 10, fontWeight: 700, color: '#b45309', background: '#fef3c7'}}>HIDDEN</span>}
                      </span>
                      {g.description && (
                        <span style={{display: 'block', fontSize: 12, color: '#64748b', marginTop: 3}}>{g.description}</span>
                      )}
                      {!g.icon && (
                        <span style={{display: 'block', fontSize: 11, color: '#94a3b8', marginTop: 3}}>No icon — mobile falls back to the category image</span>
                      )}
                    </td>

                    <td style={{padding: '14px 20px', color: '#64748b'}}>{categoryName(g.categoryId)}</td>

                    <td style={{padding: '14px 20px'}}>
                      <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                        <Toggle checked={g.isActive} onChange={() => toggleStatus(g)} />
                        <span style={{fontSize: 13, fontWeight: 500, color: g.isActive ? '#16a34a' : '#94a3b8'}}>
                          {g.isActive ? 'Active' : 'Disabled'}
                        </span>
                      </div>
                    </td>

                    <td style={{padding: '14px 20px'}}>
                      <div style={{display: 'flex', gap: 8}}>
                        <button onClick={() => openEdit(g)}
                          style={{padding: '6px 14px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, fontWeight: 500, color: '#374151', background: '#fff', cursor: 'pointer'}}>
                          Edit
                        </button>
                        <button onClick={() => setDeleteTarget(g)}
                          style={{padding: '6px 14px', border: '1px solid #fecaca', borderRadius: 7, fontSize: 13, fontWeight: 500, color: '#ef4444', background: '#fff', cursor: 'pointer'}}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {visible.length === 0 && (
                  <tr><td colSpan={7} style={{padding: '48px 20px', textAlign: 'center', color: '#94a3b8'}}>
                    No sub-categories yet — add one.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {!loading && (
          <div style={{padding: '12px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <span style={{fontSize: 13, color: '#94a3b8'}}>Showing {visible.length} of {groups.length} sub-categories</span>
            <div style={{display: 'flex', gap: 8}}>
              <span style={{padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: '#dcfce7', color: '#16a34a'}}>
                Active: {groups.filter(g => g.isActive).length}
              </span>
              <span style={{padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: '#fef3c7', color: '#b45309'}}>
                Disabled: {groups.filter(g => !g.isActive).length}
              </span>
              <span style={{padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: '#f1f5f9', color: '#64748b'}}>
                No icon: {groups.filter(g => !g.icon).length}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div onClick={() => setShowModal(false)}
          style={{position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20}}>
          <div onClick={e => e.stopPropagation()}
            style={{background: '#fff', borderRadius: 16, width: 500, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.15)'}}>
            <div style={{padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: '#fff'}}>
              <h3 style={{fontSize: 18, fontWeight: 700, color: '#0f172a', margin: 0}}>
                {editTarget ? 'Edit Sub Category' : 'Add Sub Category'}
              </h3>
              <button onClick={() => setShowModal(false)}
                style={{background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4}}>
                <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><line x1='18' y1='6' x2='6' y2='18'/><line x1='6' y1='6' x2='18' y2='18'/></svg>
              </button>
            </div>

            <div style={{padding: '24px', display: 'flex', flexDirection: 'column', gap: 18}}>
              {formError && (
                <div style={{padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 13}}>
                  {formError}
                </div>
              )}

              <div>
                <label style={labelStyle}>Parent Category <span style={{color: '#ef4444'}}>*</span></label>
                <select value={formCategoryId} onChange={e => setFormCategoryId(e.target.value)}
                  disabled={!!editTarget} style={{...inputStyle, opacity: editTarget ? 0.6 : 1}}>
                  {groupableCategories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
                <div style={{fontSize: 11, color: '#94a3b8', marginTop: 6}}>
                  {editTarget
                    ? 'Fixed after creation — moving it would orphan the items filed under it.'
                    : 'Packages categories are excluded: they sell feature checklists, which are not grouped.'}
                </div>
              </div>

              <div>
                <label style={labelStyle}>Name <span style={{color: '#ef4444'}}>*</span></label>
                <input placeholder='e.g. Battery' value={formLabel} onChange={e => setFormLabel(e.target.value)} style={inputStyle} />
              </div>

              <div>
                <label style={labelStyle}>Short description</label>
                <input placeholder='e.g. Touchless foam wash experience' value={formDescription}
                  onChange={e => setFormDescription(e.target.value)} style={inputStyle} />
                <div style={{fontSize: 11, color: '#94a3b8', marginTop: 6}}>
                  The line under the title on the Home Service card. The round filter chip shows the name alone.
                </div>
              </div>

              <div>
                <label style={labelStyle}>Show on</label>
                <div style={{display: 'flex', gap: 10}}>
                  <ShowOn
                    checked={formShowHome}
                    onChange={() => setFormShowHome(v => !v)}
                    title='Home Service'
                    hint='A card on the Home Service screen'
                  />
                  <ShowOn
                    checked={formShowPickup}
                    onChange={() => setFormShowPickup(v => !v)}
                    title='Pickup'
                    hint='A filter chip above the category’s list'
                  />
                </div>
                {!formShowHome && !formShowPickup && (
                  <div style={{fontSize: 11, color: '#b45309', marginTop: 6}}>
                    With neither ticked this sub-category is hidden from customers, though items stay filed under it.
                  </div>
                )}
              </div>

              <div>
                <label style={labelStyle}>Icon &amp; card image</label>
                <div style={{display: 'flex', gap: 14, alignItems: 'flex-start'}}>
                  <IconPicker
                    preview={formIconPreview}
                    label='Icon'
                    glyph='icon'
                    onSelect={f => { setFormIconFile(f); setFormIconPreview(URL.createObjectURL(f)); setFormRemoveIcon(false) }}
                    onRemove={() => { setFormIconFile(null); setFormIconPreview(null); setFormRemoveIcon(true) }}
                  />
                  <IconPicker
                    preview={formImagePreview}
                    label='Thumbnail image'
                    fit='cover'
                    width={200}
                    onSelect={f => { setFormImageFile(f); setFormImagePreview(URL.createObjectURL(f)); setFormRemoveImage(false) }}
                    onRemove={() => { setFormImageFile(null); setFormImagePreview(null); setFormRemoveImage(true) }}
                  />
                </div>
                <div style={{fontSize: 11, color: '#94a3b8', marginTop: 6}}>
                  Left — the icon, drawn in the filter row and as the badge beside the card title; if empty the app falls back to the category image.
                  Right — the photo across the top of the Home Service card. Max 5MB each.
                </div>
              </div>

              <div>
                <label style={labelStyle}>Display Order</label>
                <input type='number' placeholder='e.g. 1' value={formOrder}
                  onChange={e => setFormOrder(e.target.value)} style={inputStyle} />
              </div>

              {editTarget && (
                <div>
                  <label style={labelStyle}>Status</label>
                  <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                    <Toggle checked={formActive} onChange={() => setFormActive(a => !a)} />
                    <span style={{fontSize: 14, fontWeight: 500, color: formActive ? '#16a34a' : '#94a3b8'}}>
                      {formActive ? 'Active' : 'Disabled'}
                    </span>
                  </div>
                  <div style={{fontSize: 11, color: '#94a3b8', marginTop: 6}}>Disabled sub-categories are hidden from the mobile app.</div>
                </div>
              )}
            </div>

            <div style={{padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10, position: 'sticky', bottom: 0, background: '#fff'}}>
              <button onClick={() => setShowModal(false)} disabled={saving}
                style={{padding: '9px 20px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 14, cursor: 'pointer', color: '#374151'}}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                style={{padding: '9px 20px', borderRadius: 8, border: 'none', background: '#3b82f6', fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#fff', opacity: saving ? 0.7 : 1}}>
                {saving ? 'Saving...' : editTarget ? 'Save Changes' : 'Add Sub Category'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <div onClick={() => setDeleteTarget(null)}
          style={{position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20}}>
          <div onClick={e => e.stopPropagation()}
            style={{background: '#fff', borderRadius: 16, width: 420, boxShadow: '0 24px 64px rgba(0,0,0,0.15)', padding: '32px 28px', textAlign: 'center'}}>
            <div style={{width: 56, height: 56, borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px'}}>
              <svg width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='#ef4444' strokeWidth='2'>
                <polyline points='3 6 5 6 21 6'/><path d='M19 6l-1 14H6L5 6'/>
                <path d='M10 11v6M14 11v6'/><path d='M9 6V4h6v2'/>
              </svg>
            </div>
            <h3 style={{fontSize: 18, fontWeight: 700, color: '#0f172a', margin: '0 0 8px'}}>Delete Sub Category</h3>
            <p style={{fontSize: 14, color: '#64748b', margin: '0 0 24px', lineHeight: 1.6}}>
              Delete <strong style={{color: '#0f172a'}}>{deleteTarget.label}</strong>?
              Items filed under it keep working — they simply become ungrouped.
            </p>
            <div style={{display: 'flex', gap: 10, justifyContent: 'center'}}>
              <button onClick={() => setDeleteTarget(null)}
                style={{padding: '9px 24px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 14, cursor: 'pointer', color: '#374151'}}>
                Cancel
              </button>
              <button onClick={handleDelete}
                style={{padding: '9px 24px', borderRadius: 8, border: 'none', background: '#ef4444', fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#fff'}}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

const SubCategoriesPageWrapper: FC = () => <SubCategoriesPage />

export {SubCategoriesPageWrapper}
